import { useCallback, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "blueprintai_chip_rankings_v1";

/**
 * V2.0.6.1 — Personalised chip memory hook.
 *
 * Fetches the user's top chip rankings from `/api/me/chip-rankings` (with a
 * localStorage cache for instant first paint) and exposes:
 *
 *   chips        — array of chip strings to show as fallback (top 5)
 *   recordTap    — POST a "tap" event for one chip (call when user taps)
 *   recordSkip   — POST a "skip" event for a batch of chips (call when user
 *                  sends a non-chip message while these chips were visible)
 *   refresh      — re-fetch the rankings (call after recording a chip event
 *                  if you want the next render to reflect it; otherwise
 *                  rankings update on next mount)
 *
 * The recordTap / recordSkip calls are fire-and-forget — failures are logged
 * but never block the UI. The localStorage cache survives reloads.
 */
export default function useChipMemory() {
  const [chips, setChips] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const cached = window.localStorage.getItem(STORAGE_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const fetchedRef = useRef(false);

  const fetchRankings = useCallback(async () => {
    if (!API) return;
    try {
      const r = await fetch(`${API}/api/me/chip-rankings?limit=5`, {
        credentials: "include",
      });
      if (!r.ok) return;
      const data = await r.json();
      const next = (data || []).map((row) => row.chip).filter(Boolean);
      if (next.length === 0) return;
      setChips(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_) {
        // localStorage may be unavailable; safe to skip.
      }
    } catch (_) {
      // Network errors are non-fatal — we still have the cached chips.
    }
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchRankings();
  }, [fetchRankings]);

  const recordEvents = useCallback(async (events) => {
    if (!API || !events || events.length === 0) return;
    try {
      await fetch(`${API}/api/me/chip-events`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(events.slice(0, 50)),
      });
    } catch (_) {
      // fire-and-forget
    }
  }, []);

  const recordTap = useCallback(
    (chip) => recordEvents([{ chip, action: "tap" }]),
    [recordEvents],
  );

  const recordSkip = useCallback(
    (chipList) =>
      recordEvents((chipList || []).map((c) => ({ chip: c, action: "skip" }))),
    [recordEvents],
  );

  return { chips, recordTap, recordSkip, refresh: fetchRankings };
}
